import { Fragment } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import Select from 'react-select';

import type { RelatorioCivelStepProps } from './relatorio-civel-step-props';
import type { DefensoriaOption } from './relatorio-civel-step-props';

export default function Step14DesignacaoEspecialProcessos(props: RelatorioCivelStepProps) {
  const {
    podeEditar,
    novoTipoPeca,
    setNovoTipoPeca,
    novoNumeroProcessoDesig,
    setNovoNumeroProcessoDesig,
    apenasNumeros,
    defensoriasOptions,
    novaDefensoriaId,
    setNovaDefensoriaId,
    adicionarDesignacaoEspecialProcesso,
    setQuantidadeLinhas,
    quantidadeLinhas,
    adicionarMultiplasLinhasDesignacaoEspecialProcessos,
    formData,
    editandoDesignacaoEspecialProcesso,
    valorEdicaoTipoPeca,
    setValorEdicaoTipoPeca,
    valorEdicaoNumeroProcessoDesig,
    setValorEdicaoNumeroProcessoDesig,
    valorEdicaoDefensoriaId,
    setValorEdicaoDefensoriaId,
    getDefensoriaLabel,
    salvarEdicaoDesignacaoEspecialProcesso,
    cancelarEdicaoDesignacaoEspecialProcesso,
    iniciarEdicaoDesignacaoEspecialProcesso,
    removerDesignacaoEspecialProcesso,
  } = props;

  return (
    <Fragment>
      {podeEditar && (
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-bold">Tipo de Peça</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ex: Petição Inicial"
                value={novoTipoPeca}
                onChange={(e) => setNovoTipoPeca(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-bold">N.º do Processo</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ex: 000000.000.000"
                value={novoNumeroProcessoDesig}
                onChange={(e) => setNovoNumeroProcessoDesig(apenasNumeros(e.target.value))}
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold">Defensoria do Processo/Vara</Form.Label>
              <Select
                inputId="defensoriaProcessoVara"
                options={defensoriasOptions}
                isClearable
                placeholder="Selecione..."
                value={novaDefensoriaId ? defensoriasOptions.find((o) => o.value === novaDefensoriaId) : null}
                onChange={(opt) => setNovaDefensoriaId((opt as DefensoriaOption | null)?.value ?? null)}
              />
            </Form.Group>
          </Col>
          <Col md={1} className="d-flex align-items-end">
            <Button
              variant="primary"
              onClick={adicionarDesignacaoEspecialProcesso}
              style={{ borderRadius: '8px' }}
              className="w-100"
            >
              <i className="bi bi-plus-lg me-2"></i>
              Adicionar
            </Button>
          </Col>
        </Row>
      )}

      {podeEditar && (
        <Row className="mb-3">
          <Col md={6}>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted">Adicionar</span>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() =>
                  setQuantidadeLinhas((prev) => ({
                    ...prev,
                    designacaoEspecialProcessos: Math.max(1, prev.designacaoEspecialProcessos - 1),
                  }))
                }
                style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
              >
                <i className="bi bi-dash">-</i>
              </Button>
              <Form.Control
                type="number"
                min="1"
                max="50"
                value={quantidadeLinhas.designacaoEspecialProcessos}
                onChange={(e) =>
                  setQuantidadeLinhas((prev) => ({
                    ...prev,
                    designacaoEspecialProcessos: Math.max(1, parseInt(e.target.value) || 1),
                  }))
                }
                style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
                size="sm"
              />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() =>
                  setQuantidadeLinhas((prev) => ({
                    ...prev,
                    designacaoEspecialProcessos: Math.min(50, prev.designacaoEspecialProcessos + 1),
                  }))
                }
                style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
              >
                <i className="bi bi-plus">+</i>
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={adicionarMultiplasLinhasDesignacaoEspecialProcessos}
                style={{ borderRadius: '8px' }}
              >
                <i className="bi bi-plus-lg me-1"></i>
                Adicionar {quantidadeLinhas.designacaoEspecialProcessos} linha
                {quantidadeLinhas.designacaoEspecialProcessos > 1 ? 's' : ''}
              </Button>
            </div>
          </Col>
          <Col md={6} className="d-flex align-items-center justify-content-end">
            <h5 className="mb-0 text-primary">
              <small className="text-muted">Total de Itens:</small> {formData.designacaoEspecialProcessos.itens.length}
            </h5>
          </Col>
        </Row>
      )}

      <Table bordered hover>
        <thead className="table-light">
          <tr>
            <th style={{ width: '5%' }}>#</th>
            <th style={{ width: '25%' }}>Tipo de Peça</th>
            <th style={{ width: '25%' }}>N.º do Processo</th>
            <th style={{ width: '25%' }}>Defensoria do Processo/Vara</th>
            {podeEditar && <th style={{ width: '20%' }}>Ações</th>}
          </tr>
        </thead>
        <tbody>
          {formData.designacaoEspecialProcessos.itens.length === 0 ? (
            <tr>
              <td colSpan={podeEditar ? 5 : 4} className="text-center text-muted">
                Nenhum item adicionado ainda
              </td>
            </tr>
          ) : (
            formData.designacaoEspecialProcessos.itens.map((item, index: number) => {
              const estaEditando = editandoDesignacaoEspecialProcesso === item.id;

              return (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>
                    {estaEditando ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Ex: Petição Inicial"
                        value={valorEdicaoTipoPeca}
                        onChange={(e) => setValorEdicaoTipoPeca(e.target.value)}
                        style={{ borderRadius: '8px' }}
                      />
                    ) : (
                      item.tipoPeca
                    )}
                  </td>
                  <td>
                    {estaEditando ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Ex: 000000.000.000"
                        value={valorEdicaoNumeroProcessoDesig}
                        onChange={(e) => setValorEdicaoNumeroProcessoDesig(apenasNumeros(e.target.value))}
                        onKeyPress={(e) => {
                          if (!/[0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        style={{ borderRadius: '8px' }}
                      />
                    ) : (
                      item.numeroProcesso
                    )}
                  </td>
                  <td>
                    {estaEditando ? (
                      <Select
                        inputId={`defensoriaProcessoVara-${item.id}`}
                        options={defensoriasOptions}
                        isClearable
                        placeholder="Selecione..."
                          value={
                            valorEdicaoDefensoriaId
                              ? defensoriasOptions.find((o) => o.value === valorEdicaoDefensoriaId)
                              : null
                          }
                        onChange={(opt) => setValorEdicaoDefensoriaId((opt as DefensoriaOption | null)?.value ?? null)}
                      />
                    ) : (
                      item.defensoriaLabel || getDefensoriaLabel(item.defensoriaId)
                    )}
                  </td>
                  {podeEditar && (
                    <td>
                      {estaEditando ? (
                        <div className="d-flex gap-1">
                          <Button
                            style={{ width: '80px', height: '40px' }}
                            variant="success"
                            size="sm"
                            onClick={() => salvarEdicaoDesignacaoEspecialProcesso(item.id)}
                            title="Salvar"
                          >
                            <i className="bi bi-check-lg"> Salvar</i>
                          </Button>
                          <Button
                            style={{ width: '80px', height: '40px' }}
                            variant="secondary"
                            size="sm"
                            onClick={cancelarEdicaoDesignacaoEspecialProcesso}
                            title="Cancelar"
                          >
                            <i className="bi bi-x-lg"> Cancelar</i>
                          </Button>
                        </div>
                      ) : (
                        <div className="d-flex gap-1">
                          <Button
                            style={{ width: '80px', height: '40px' }}
                            variant="outline-primary"
                            size="sm"
                            onClick={() => iniciarEdicaoDesignacaoEspecialProcesso(item)}
                            title="Editar"
                          >
                            <i className="bi bi-pencil"> Editar</i>
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removerDesignacaoEspecialProcesso(item.id)}
                            style={{ width: '80px', height: '40px' }}
                            title="Excluir"
                          >
                            <i className="bi bi-trash"> Excluir</i>
                          </Button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </Fragment>
  );
}

